import {
  GroupResponse,
  MemberResponse,
  RoleResponse,
} from '@modusign/openapi-client-user/generated/model';
import { Merge } from 'ts-essentials';

export type Prettify<T> = {
  [K in keyof T]: T[K];
  // eslint-disable-next-line @typescript-eslint/ban-types
} & {};

type Nullable<T> = T | null;

export type WorkspaceMember = {
  subjectType: 'MEMBER';
  subjectId: string;
  memberName: string;
  memberEmail: string;
};
export function adaptWorkspaceMember<T extends Pick<MemberResponse, 'id' | 'name' | 'userEmail'>>(
  member: T
): Merge<Omit<T, 'id' | 'name' | 'userEmail'>, WorkspaceMember> {
  const { id, name, userEmail, ...rest } = member;
  return {
    subjectType: 'MEMBER' as const,
    subjectId: id,
    memberName: name,
    memberEmail: userEmail,
    ...rest,
  };
}

export type WorkspaceGroup = {
  subjectType: 'GROUP';
  subjectId: string;
  groupType: 'ALL_MEMBERS' | 'CUSTOM';
  groupName: string;
};
export function adaptWorkspaceGroup<T extends Pick<GroupResponse, 'id' | 'name' | 'type'>>(
  group: T
): Merge<Omit<T, 'id' | 'name' | 'type'>, WorkspaceGroup> {
  const { id, name, type, ...rest } = group;
  return {
    subjectType: 'GROUP' as const,
    subjectId: id,
    groupName: name,
    groupType: type,
    ...rest,
  };
}

export type WorkspaceRole = Prettify<RoleResponse>;

export type ResourceRoleSubject = WorkspaceMember | WorkspaceGroup;
export function adaptResourceRoleSubject<
  T extends {
    subjectType: 'MEMBER' | 'GROUP';
    id: string;
    member?: Nullable<Pick<MemberResponse, 'name' | 'userEmail'>>;
    group?: Nullable<Pick<GroupResponse, 'name' | 'type'>>;
  },
>(subject: T): Merge<Omit<T, 'id' | 'member' | 'group'>, ResourceRoleSubject> {
  if (subject.subjectType === 'MEMBER') {
    const { subjectType, id, member, ...rest } = subject;
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const { name, userEmail, ...memberRest } = member!;
    return {
      subjectType,
      subjectId: id,
      memberName: name,
      memberEmail: userEmail,
      ...memberRest,
      ...rest,
    };
  }
  const { subjectType, id, group, ...rest } = subject;
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { name, type, ...groupRest } = group!;
  return {
    subjectType,
    subjectId: id,
    groupName: name,
    groupType: type,
    ...groupRest,
    ...rest,
  };
}
